import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Film, Image, Facebook, Loader2, RefreshCw, Heart, MessageCircle, Send, Bookmark, MoreHorizontal, ThumbsUp, Share2, Info, ToggleRight } from 'lucide-react';
import TabStories from './TabStories';
import { SettingsSection, SettingsToggle, SettingsSelect } from '../index';
import { generateFeedPreview } from '../../../services/storyApi';
import { DEFAULT_PUBLICATION_EVENTS, PUBLICATION_EVENT_TYPES } from '../../../constants/settings';

const SUB_TABS = [
  { id: 'stories', label: 'Stories', icon: Film },
  { id: 'feed_instagram', label: 'Feed Instagram', icon: Image },
  { id: 'feed_facebook', label: 'Feed Facebook', icon: Facebook },
];

const CROP_OPTIONS_IG = [
  { value: 'square', label: 'Carré (1:1) — recommandé' },
  { value: 'portrait', label: 'Portrait (4:5)' },
  { value: 'original', label: 'Format original' },
];

const CROP_OPTIONS_FB = [
  { value: 'original', label: 'Format original — recommandé' },
  { value: 'landscape', label: 'Paysage (16:9)' },
  { value: 'square', label: 'Carré (1:1)' },
];

/* ============================================================
 * ChannelStatusBanner — shows which events are enabled/disabled
 * for a given publication channel (instagram_post, facebook_post)
 * ============================================================ */
const EVENT_LABELS = Object.fromEntries(PUBLICATION_EVENT_TYPES.map(e => [e.id, e.label]));

const ChannelStatusBanner = ({ settings, onChange, channelId, channelLabel }) => {
  const pubEvents = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;

  const enabled = [];
  const disabled = [];
  PUBLICATION_EVENT_TYPES.forEach(({ id }) => {
    const cfg = pubEvents[id] || {};
    (cfg[channelId] ? enabled : disabled).push(id);
  });

  const allEnabled = disabled.length === 0;
  const noneEnabled = enabled.length === 0;

  const toggleEvent = (eventId) => {
    const current = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
    const eventConfig = current[eventId] || {};
    onChange({
      ...settings,
      publication_events: {
        ...current,
        [eventId]: { ...eventConfig, [channelId]: !eventConfig[channelId] },
      },
    });
  };

  const toggleAll = () => {
    const current = settings?.publication_events || DEFAULT_PUBLICATION_EVENTS;
    const updated = { ...current };
    disabled.forEach(eventId => {
      updated[eventId] = { ...(updated[eventId] || {}), [channelId]: true };
    });
    onChange({ ...settings, publication_events: updated });
  };

  if (allEnabled) {
    return (
      <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg text-[13px] leading-relaxed bg-emerald-500/5 border border-emerald-500/15 text-emerald-400/90 animate-[fadeIn_0.2s_ease-out]">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
        <span>
          La publication {channelLabel} est <strong className="font-medium text-emerald-400">activée</strong> pour tous les événements.
        </span>
      </div>
    );
  }

  if (noneEnabled) {
    return (
      <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg text-[13px] leading-relaxed bg-amber-500/5 border border-amber-500/15 text-amber-400/90 animate-[fadeIn_0.2s_ease-out]">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
        <div className="flex-1 min-w-0">
          <span>
            La publication {channelLabel} n'est activée pour aucun événement. Vos réglages sont sauvegardés mais rien ne sera publié.{' '}
            <button
              onClick={toggleAll}
              className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 font-medium underline underline-offset-2 decoration-amber-300/30 hover:decoration-amber-200/50 transition-colors"
            >
              <ToggleRight className="h-3.5 w-3.5" />
              Tout activer
            </button>
          </span>
        </div>
      </div>
    );
  }

  // Mixed: some enabled, some disabled
  return (
    <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg text-[13px] leading-relaxed bg-amber-500/5 border border-amber-500/15 text-amber-400/90 animate-[fadeIn_0.2s_ease-out]">
      <Info className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70" />
      <div className="flex-1 min-w-0">
        <span>
          Publication {channelLabel} activée pour{' '}
          <strong className="font-medium text-emerald-400">{enabled.map(id => EVENT_LABELS[id]).join(', ')}</strong>
          {' '}— pas encore pour{' '}
          <strong className="font-medium text-amber-300">{disabled.map(id => EVENT_LABELS[id]).join(', ')}</strong>.
        </span>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {disabled.map(eventId => (
            <button
              key={eventId}
              onClick={() => toggleEvent(eventId)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-400/10 text-amber-300 hover:bg-amber-400/20 transition-colors"
            >
              <ToggleRight className="h-3 w-3" />
              {EVENT_LABELS[eventId]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============================================================
 * Feed Preview Component (shared by IG and FB)
 * ============================================================ */
const FeedPreview = ({ platform, refreshTrigger, autoHeight }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, refreshTrigger]);

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await generateFeedPreview(platform);
      setPreviewUrl(result.preview_url + '?t=' + Date.now());
    } catch (err) {
      console.error('Error loading feed preview:', err);
      setError('Erreur de preview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`w-full flex items-center justify-center bg-dark-hover ${autoHeight ? 'py-12' : 'h-full'}`}>
        <Loader2 className="h-6 w-6 animate-spin text-carmin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-full flex items-center justify-center bg-dark-hover text-muted text-sm ${autoHeight ? 'py-12' : 'h-full'}`}>
        {error}
      </div>
    );
  }

  return (
    <img
      src={previewUrl}
      alt="Feed preview"
      className={autoHeight ? 'w-full block' : 'w-full h-full object-contain'}
    />
  );
};

/* ============================================================
 * TabTemplates - Main wrapper
 * ============================================================ */
const TabTemplates = ({ settings, onChange }) => {
  const [activeSubTab, setActiveSubTab] = useState('stories');

  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 p-1 bg-dark-surface rounded-lg">
        {SUB_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                isActive
                  ? 'bg-dark-card text-carmin'
                  : 'text-muted hover:text-label'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'stories' && (
        <TabStories settings={settings} onChange={onChange} />
      )}

      {activeSubTab === 'feed_instagram' && (
        <FeedInstagramSettings settings={settings} updateSetting={updateSetting} onChange={onChange} />
      )}

      {activeSubTab === 'feed_facebook' && (
        <FeedFacebookSettings settings={settings} updateSetting={updateSetting} onChange={onChange} />
      )}
    </div>
  );
};

/* ============================================================
 * Feed Instagram Settings (with preview)
 * ============================================================ */
const FeedInstagramSettings = ({ settings, updateSetting, onChange }) => {
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const debounceRef = useRef(null);

  // Refresh preview when relevant settings change
  const triggerPreviewRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewTrigger(prev => prev + 1);
    }, 800);
  }, []);

  const handleSettingChange = (key, value) => {
    updateSetting(key, value);
    triggerPreviewRefresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-8">
      {/* Controls */}
      <div className="space-y-6">
        <ChannelStatusBanner settings={settings} onChange={onChange} channelId="instagram_post" channelLabel="Feed Instagram" />
        <SettingsSection title="Format des images">
          <div className="space-y-6">
            <SettingsSelect
              label="Format de recadrage"
              description="Format appliqué aux photos avant publication dans le feed Instagram"
              value={settings?.ig_feed_crop || 'square'}
              onChange={(value) => handleSettingChange('ig_feed_crop', value)}
              options={CROP_OPTIONS_IG}
            />

            <SettingsToggle
              label="Recadrage intelligent"
              description="Centrer automatiquement sur le véhicule lors du recadrage"
              checked={settings?.ig_feed_smart_crop !== false}
              onChange={(value) => handleSettingChange('ig_feed_smart_crop', value)}
            />
          </div>
        </SettingsSection>

        <SettingsSection title="Watermark">
          <div className="space-y-6">
            <SettingsToggle
              label="Appliquer le watermark"
              description="Ajouter votre logo en filigrane sur les photos du feed"
              checked={settings?.watermark_enabled || false}
              onChange={(value) => handleSettingChange('watermark_enabled', value)}
            />

            {settings?.watermark_enabled && (
              <>
                <SettingsSelect
                  label="Position du watermark"
                  value={settings?.watermark_position || 'bottom-right'}
                  onChange={(value) => handleSettingChange('watermark_position', value)}
                  options={[
                    { value: 'bottom-right', label: 'En bas à droite' },
                    { value: 'bottom-left', label: 'En bas à gauche' },
                    { value: 'top-right', label: 'En haut à droite' },
                    { value: 'top-left', label: 'En haut à gauche' },
                  ]}
                />

                <div>
                  <label className="block text-sm font-medium text-label mb-2">
                    Opacité : {Math.round((settings?.watermark_opacity || 0.8) * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={settings?.watermark_opacity || 0.8}
                    onChange={(e) => handleSettingChange('watermark_opacity', parseFloat(e.target.value))}
                    className="w-full accent-carmin"
                  />
                </div>
              </>
            )}

            {!settings?.logo_url && settings?.watermark_enabled && (
              <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                <p className="text-sm text-amber-400">
                  Uploadez un logo dans Mon entreprise pour activer le watermark.
                </p>
              </div>
            )}
          </div>
        </SettingsSection>

        <SettingsSection title="Nombre de photos">
          <div className="space-y-6">
            <SettingsSelect
              label="Photos par publication"
              description="Nombre maximum de photos dans un carousel Instagram"
              value={String(settings?.ig_feed_max_images || 10)}
              onChange={(value) => updateSetting('ig_feed_max_images', parseInt(value))}
              options={[
                { value: '1', label: '1 photo' },
                { value: '3', label: '3 photos' },
                { value: '5', label: '5 photos' },
                { value: '10', label: '10 photos (max carousel)' },
              ]}
            />
          </div>
        </SettingsSection>
      </div>

      {/* Preview */}
      <div className="lg:sticky lg:top-24 self-start h-fit">
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-label">
              Aperçu Feed Instagram
            </label>
            <button
              onClick={() => setPreviewTrigger(prev => prev + 1)}
              className="p-1 text-secondary hover:text-label"
              title="Actualiser le preview"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-center">
            <div style={{
              width: 250,
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              background: '#ffffff',
            }}>
              {/* IG Header */}
              <div className="flex items-center gap-2 px-2.5 py-2">
                <div className="w-7 h-7 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', padding: '1.5px' }}>
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <div className="w-5 h-5 rounded-full bg-gray-300" />
                  </div>
                </div>
                <span className="text-[11px] font-semibold text-gray-900 flex-1 truncate">
                  {settings?.company_name?.toLowerCase().replace(/\s+/g, '_') || 'votre_garage'}
                </span>
                <MoreHorizontal className="h-4 w-4 text-gray-900 flex-shrink-0" />
              </div>
              {/* Image */}
              <div style={{
                width: 250,
                ...(settings?.ig_feed_crop !== 'original' && {
                  height: settings?.ig_feed_crop === 'portrait' ? 312 : 250,
                }),
              }}>
                <FeedPreview platform="instagram" refreshTrigger={previewTrigger} autoHeight={settings?.ig_feed_crop === 'original'} />
              </div>
              {/* IG Footer */}
              <div className="px-2.5 pt-2 pb-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-3">
                    <Heart className="h-5 w-5 text-gray-900" />
                    <MessageCircle className="h-5 w-5 text-gray-900" />
                    <Send className="h-5 w-5 text-gray-900" />
                  </div>
                  <Bookmark className="h-5 w-5 text-gray-900" />
                </div>
                <p className="text-[11px] font-semibold text-gray-900 mb-0.5">42 J'aime</p>
                <p className="text-[11px] text-gray-900 leading-tight">
                  <span className="font-semibold">{settings?.company_name?.toLowerCase().replace(/\s+/g, '_') || 'votre_garage'}</span>
                  {' '}Nouvelle arrivée !
                </p>
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted text-center">
            Recadrage {settings?.ig_feed_crop === 'portrait' ? '4:5' : settings?.ig_feed_crop === 'original' ? 'original' : '1:1'}
            {settings?.watermark_enabled ? ' + watermark' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
 * Feed Facebook Settings (with preview)
 * ============================================================ */
const FeedFacebookSettings = ({ settings, updateSetting, onChange }) => {
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const debounceRef = useRef(null);

  const triggerPreviewRefresh = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewTrigger(prev => prev + 1);
    }, 800);
  }, []);

  const handleSettingChange = (key, value) => {
    updateSetting(key, value);
    triggerPreviewRefresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-8">
      {/* Controls */}
      <div className="space-y-6">
        <ChannelStatusBanner settings={settings} onChange={onChange} channelId="facebook_post" channelLabel="Feed Facebook" />
        <SettingsSection title="Format des images">
          <div className="space-y-6">
            <SettingsSelect
              label="Format de recadrage"
              description="Format appliqué aux photos avant publication sur Facebook"
              value={settings?.fb_feed_crop || 'original'}
              onChange={(value) => handleSettingChange('fb_feed_crop', value)}
              options={CROP_OPTIONS_FB}
            />
          </div>
        </SettingsSection>

        <SettingsSection title="Watermark">
          <div className="space-y-6">
            <SettingsToggle
              label="Appliquer le watermark"
              description="Même watermark que le feed Instagram"
              checked={settings?.watermark_enabled || false}
              onChange={(value) => handleSettingChange('watermark_enabled', value)}
            />
          </div>
        </SettingsSection>

        <SettingsSection title="Nombre de photos">
          <div className="space-y-6">
            <SettingsSelect
              label="Photos par publication"
              description="Nombre maximum de photos dans un album Facebook"
              value={String(settings?.fb_feed_max_images || 10)}
              onChange={(value) => updateSetting('fb_feed_max_images', parseInt(value))}
              options={[
                { value: '1', label: '1 photo' },
                { value: '3', label: '3 photos' },
                { value: '5', label: '5 photos' },
                { value: '10', label: '10 photos' },
                { value: '20', label: '20 photos' },
              ]}
            />
          </div>
        </SettingsSection>
      </div>

      {/* Preview */}
      <div className="lg:sticky lg:top-24 self-start h-fit">
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-label">
              Aperçu Feed Facebook
            </label>
            <button
              onClick={() => setPreviewTrigger(prev => prev + 1)}
              className="p-1 text-secondary hover:text-label"
              title="Actualiser le preview"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-center">
            <div style={{
              width: 250,
              borderRadius: 8,
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
              background: '#242526',
            }}>
              {/* FB Header */}
              <div className="flex items-center gap-2 px-2.5 py-2">
                <div className="w-8 h-8 rounded-full bg-gray-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-primary truncate leading-tight">
                    {settings?.company_name || 'Votre Garage'}
                  </p>
                  <p className="text-[10px] text-secondary leading-tight">
                    Sponsorisé · 2h · 🌐
                  </p>
                </div>
                <MoreHorizontal className="h-4 w-4 text-secondary flex-shrink-0" />
              </div>
              {/* Image */}
              <div style={{
                width: 250,
                ...(settings?.fb_feed_crop !== 'original' && {
                  height: settings?.fb_feed_crop === 'square' ? 250 : 141,
                }),
              }}>
                <FeedPreview platform="facebook" refreshTrigger={previewTrigger} autoHeight={settings?.fb_feed_crop === 'original'} />
              </div>
              {/* FB Reactions */}
              <div className="px-2.5 pt-1.5 pb-1">
                <div className="flex items-center justify-between text-[10px] text-secondary pb-1.5" style={{ borderBottom: '1px solid #3a3b3c' }}>
                  <div className="flex items-center gap-1">
                    <span>👍</span>
                    <span>❤️</span>
                    <span>12</span>
                  </div>
                  <span>3 commentaires</span>
                </div>
              </div>
              {/* FB Action buttons */}
              <div className="flex items-center justify-around px-2 pb-2">
                <div className="flex items-center gap-1 text-secondary">
                  <ThumbsUp className="h-3.5 w-3.5" />
                  <span className="text-[10px]">J'aime</span>
                </div>
                <div className="flex items-center gap-1 text-secondary">
                  <MessageCircle className="h-3.5 w-3.5" />
                  <span className="text-[10px]">Commenter</span>
                </div>
                <div className="flex items-center gap-1 text-secondary">
                  <Share2 className="h-3.5 w-3.5" />
                  <span className="text-[10px]">Partager</span>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted text-center">
            Format {settings?.fb_feed_crop === 'square' ? '1:1' : settings?.fb_feed_crop === 'landscape' ? '16:9' : 'original'}
            {settings?.watermark_enabled ? ' + watermark' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TabTemplates;
